/* eslint-disable jira/filename-case */
import type { ComponentType, MutableRefObject, Ref } from 'react';
import type { EntryPoint, EntryPointComponent } from 'react-relay';
import type { EntryPointRouteParams } from '@jira/platform__entry-points-plugin-types/src/types.tsx';
import type { PopupProps } from '@atlaskit/popup/types';
import {
	type GLOBAL_APP_MENU_ID,
	CONFIGURABLE_MENU_ID,
} from '@jira/navigation-apps__sidebar-nav4-context/src/common/constants.tsx';

// eslint-disable-next-line jira/restricted/@atlassian+jira-entry-point
import type { ReferenceFromEntryPoint } from '@jira/platform__entry-point/src/common/types.tsx';
import type { CustomItemProps } from '@atlassian/navigation-common/types';

export type AsyncAction = () => Promise<void>;

const OPERATIONS_MENU_ID = {
	OPERATIONS_ALERT_SAVED_SEARCH_SHARED_WITH_ME:
		'jira.sidebar.operations.alert.savedSearches.sharedWithMe',
	OPERATIONS_ALERT_SAVED_SEARCH_CREATED_BY_ME:
		'jira.sidebar.operations.alert.savedSearches.createdByMe',
} as const;

type ConfigurableMenuIdKey = keyof typeof CONFIGURABLE_MENU_ID;
export type ConfigurableMenuId = (typeof CONFIGURABLE_MENU_ID)[ConfigurableMenuIdKey];

type GlobalAppMenuIdKey = keyof typeof GLOBAL_APP_MENU_ID;
export type GlobalAppMenuId = (typeof GLOBAL_APP_MENU_ID)[GlobalAppMenuIdKey];

export type JiraConfigurableNavigationItem = {
	readonly menuId: string;
	readonly isVisible: boolean;
};

type FilterableMenuIdKey = keyof typeof FILTERABLE_MENU_ID;
export type FilterableMenuId = (typeof FILTERABLE_MENU_ID)[FilterableMenuIdKey];

export const FILTERABLE_MENU_ID: {
	readonly BOARDS: 'jira.sidebar.boards';
	readonly QUEUES: 'jira.sidebar.queues';
	readonly ALL: 'jira.sidebar.all';
	readonly GLOBAL_APP_GOALS: 'jira.sidebar.globalApp.goals';
	readonly GLOBAL_APP_ASSETS: 'jira.sidebar.globalApp.assets';
	readonly GLOBAL_APP_CONFLUENCE: 'jira.sidebar.globalApp.confluence';
	readonly GLOBAL_APP_LOOM: 'jira.sidebar.globalApp.loom';
	readonly GLOBAL_APP_PROJECTS: 'jira.sidebar.globalApp.projects';
	readonly GLOBAL_APP_TEAMS: 'jira.sidebar.globalApp.teams';
	readonly GLOBAL_APP_OPSGENIE: 'jira.sidebar.globalApp.opsgenie';
	readonly APPS: 'jira.sidebar.apps';
	readonly ASSETS: 'jira.sidebar.assets';
	readonly DASHBOARDS: 'jira.sidebar.dashboards';
	readonly FILTERS: 'jira.sidebar.filters';
	readonly GOALS: 'jira.sidebar.goals';
	readonly OPERATIONS: 'jira.sidebar.operations';
	readonly PLANS: 'jira.sidebar.plans';
	readonly PROJECTS: 'jira.sidebar.projects';
	readonly RECENT: 'jira.sidebar.recent';
	readonly STARRED: 'jira.sidebar.starred';
	readonly TEAMS: 'jira.sidebar.teams';
	readonly ROADMAPS: 'jira.sidebar.roadmaps';
	readonly CUSTOMER_SERVICE: 'jira.sidebar.customerService';
	readonly CUSTOMER_SERVICE_ANALYTICS: 'jira.sidebar.customerServiceAnalytics';
	readonly CUSTOMERS: 'jira.sidebar.customers';
	readonly ITAM: 'jira.sidebar.itam';
} = {
	...CONFIGURABLE_MENU_ID,
	BOARDS: 'jira.sidebar.boards',
	QUEUES: 'jira.sidebar.queues',
	ALL: 'jira.sidebar.all',
} as const;

export const L1_MENU_ID: {
	readonly OPERATIONS_ALERT_SAVED_SEARCH_SHARED_WITH_ME: 'jira.sidebar.operations.alert.savedSearches.sharedWithMe';
	readonly OPERATIONS_ALERT_SAVED_SEARCH_CREATED_BY_ME: 'jira.sidebar.operations.alert.savedSearches.createdByMe';
	readonly GLOBAL_APP_GOALS: 'jira.sidebar.globalApp.goals';
	readonly GLOBAL_APP_ASSETS: 'jira.sidebar.globalApp.assets';
	readonly GLOBAL_APP_CONFLUENCE: 'jira.sidebar.globalApp.confluence';
	readonly GLOBAL_APP_LOOM: 'jira.sidebar.globalApp.loom';
	readonly GLOBAL_APP_PROJECTS: 'jira.sidebar.globalApp.projects';
	readonly GLOBAL_APP_TEAMS: 'jira.sidebar.globalApp.teams';
	readonly GLOBAL_APP_OPSGENIE: 'jira.sidebar.globalApp.opsgenie';
	readonly APPS: 'jira.sidebar.apps';
	readonly ASSETS: 'jira.sidebar.assets';
	readonly DASHBOARDS: 'jira.sidebar.dashboards';
	readonly FILTERS: 'jira.sidebar.filters';
	readonly GOALS: 'jira.sidebar.goals';
	readonly OPERATIONS: 'jira.sidebar.operations';
	readonly PLANS: 'jira.sidebar.plans';
	readonly PROJECTS: 'jira.sidebar.projects';
	readonly RECENT: 'jira.sidebar.recent';
	readonly STARRED: 'jira.sidebar.starred';
	readonly TEAMS: 'jira.sidebar.teams';
	readonly ROADMAPS: 'jira.sidebar.roadmaps';
	readonly CUSTOMER_SERVICE: 'jira.sidebar.customerService';
	readonly CUSTOMER_SERVICE_ANALYTICS: 'jira.sidebar.customerServiceAnalytics';
	readonly CUSTOMERS: 'jira.sidebar.customers';
	readonly ITAM: 'jira.sidebar.itam';
	readonly YOUR_WORK: 'jira.sidebar.yourWork';
} = {
	YOUR_WORK: 'jira.sidebar.yourWork',
	...CONFIGURABLE_MENU_ID,
	...OPERATIONS_MENU_ID,
} as const;

type MenuIdKey = keyof typeof L1_MENU_ID;
export type MenuId = (typeof L1_MENU_ID)[MenuIdKey];

// A nav menu button can appear in a flyout and may need access to its `onClose`.
export type NavMenuButtonProps = OnCloseProps;

// A collection of nav menu buttons can appear in a flyout and may need access to its `onClose`.
type NavMenuButtonsProps = OnCloseProps;

export type NavMenuButtons = ComponentType<NavMenuButtonsProps>;

// A nav submenu can appear in a flyout and may need access to its `onClose`.
type NavSubmenuFooterProps = OnCloseProps;

export type NavSubmenuFooter = ComponentType<NavSubmenuFooterProps>;
export type NavSubmenuHeaderProps = OnCloseProps;
export type NavSubmenuHeader = ComponentType<NavSubmenuHeaderProps>;
type NavSubmenuMenuItemsProps = OnCloseProps;
export type NavSubmenuMenuItems = ComponentType<NavSubmenuMenuItemsProps>;

export type OnClose = NonNullable<PopupProps['onClose']>;
export type OnCloseProps = { onClose?: OnClose };

// This is a temporary type used for filtering recent and starred results
export type NavigationFilters = {
	name: string;
	id: FilterableMenuId;
};

export type LeftSidebarEntryPointConfig = ReferenceFromEntryPoint<
	EntryPoint<EntryPointComponent<{}, Record<string, never>, {}, {}>, EntryPointRouteParams>
>;

export type MenuItemConfigData = {
	menuId: string;
	visible: boolean;
};

export interface Nav4DragAndDropMenuItemProps extends Pick<
	CustomItemProps,
	// | 'draggableRef'
	| 'dropIndicator'
	| 'hasDragIndicator'
	| 'isDragging'
	| 'isExpandOrFlyoutOpen'
	| 'onExpandFlyoutToggle'
	// | 'visualContentRef'
> {
	// Adjust these types to match Jira requirements
	draggableRef?: Ref<HTMLAnchorElement | HTMLButtonElement>;
	visualContentRef?: MutableRefObject<HTMLDivElement | null>;
}
